import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { searchApolloLeads } from '../apollo-api.js';
import { ConnectionNoteNotice } from '../connection-note-notice.js';
import { sourcingSearch } from '../sourcing-search.js';
import { toast } from '../toast.js';
import {
  describeSteps,
  improveRequest,
  LINKEDIN_CONNECT_NOTE_MAX_CHARS,
  laneOf,
  newStep,
  normalizeConditions,
  previewContentKey,
  previewRequest,
  type SampleLead,
  type StepDescriptor,
  sequenceAccountErrors,
  sequenceWarnings,
  sourcedSampleLead,
  unitWord,
  uploadedSampleLead,
  withoutStep,
  withStepInserted,
  withStepMoved,
} from './logic/sequence.js';
import './prompt-editor.js';
import type { PromptEditorElement } from './prompt-editor.js';
import type { DelayUnit, OfferingConfig, SequenceChannel, SequenceStep } from './types.js';
import {
  connectStepCarriesANote,
  defaultEmailSequence,
  defaultLinkedinSequence,
  defaultMultiSequence,
  OUTREACH_LANGUAGES,
  SENDER_CHIP_THEME,
  SENDER_VARIABLES,
  TEMPLATE_VARIABLES,
  VARIABLE_CHIP_THEME,
} from './types.js';

export { LINKEDIN_CONNECT_NOTE_MAX_CHARS, type SampleLead, type StepDescriptor };
import type { LeadRecord } from './logic/leads.js';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface WizSequenceProps {
  sequence: SequenceStep[];
  leads: LeadRecord[];
  offering: OfferingConfig;
  onSequenceChange: (sequence: SequenceStep[]) => void;
  autoReply?: boolean;
  onAutoReplyChange?: (enabled: boolean) => void;
  replyPrompt?: string;
  onReplyPromptChange?: (prompt: string) => void;
  /** Whether any assigned Sales Representative has an email account connected. */
  hasEmailAccount?: boolean;
  /** Whether any assigned Sales Representative has a LinkedIn account connected. */
  hasLinkedinAccount?: boolean;
  /**
   * The LinkedIn accounts this draft actually sends from, for the connection-
   * note notice. Not derivable here: `hasLinkedinAccount` answers whether the
   * reps are covered, which is a different question from WHICH accounts they
   * cover it with, and the notice names accounts.
   */
  linkedinAccountIds?: readonly string[];
  /**
   * The campaign's selected Sales Rep (see `previewSenderId`), passed to the
   * message preview so it signs off as that rep. Without it the preview fell
   * back to the user's default rep — and, when none was set, to a stand-in
   * persona; since REP1 the server then writes no preview at all.
   */
  previewAiEmployeeId?: string;
  /**
   * Apollo sourcing criteria of the Leads step. When no leads were uploaded,
   * one matching lead is fetched so message previews use a REAL person
   * instead of “Hi there” placeholder copy.
   */
  apolloSearchInput?: Record<string, unknown>;
  /** Outreach language (ISO code, see OUTREACH_LANGUAGES). Default 'en'. */
  language?: string;
  onLanguageChange?: (language: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const DELAY_UNITS: { value: DelayUnit; label: string }[] = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

const CHANNELS_ALL: { value: SequenceChannel; label: string }[] = [
  { value: 'email', label: '✉️ Email' },
  { value: 'linkedin_connect', label: '🔗 LinkedIn Connect' },
  { value: 'linkedin_dm', label: '💬 LinkedIn DM' },
];

// ---------------------------------------------------------------------------
// Channel presentation metadata (drives the lane layout + compact cards).
// Colors map to the NDI brand tokens: email=primary, linkedin_connect=secondary,
// linkedin_dm=accent (same convention used across campaign-detail.ts).
// ---------------------------------------------------------------------------
interface ChannelMeta {
  label: string;
  previewLabel: string;
  suggestedLabel: string;
  icon: string;
  /** solid number badge */
  badge: string;
  /** channel chip — the same quiet grey for every channel; the icon and name tell them apart */
  chip: string;
}

const CHANNEL_META: Record<SequenceChannel, ChannelMeta> = {
  email: {
    label: 'Email',
    previewLabel: 'Email Message',
    suggestedLabel: 'Suggested Email',
    icon: 'icon-[material-symbols--mail-outline]',
    badge: 'bg-base-200 text-base-content/70',
    chip: 'bg-base-200 text-base-content/70',
  },
  linkedin_connect: {
    label: 'LinkedIn Connect',
    previewLabel: 'LinkedIn Request Message',
    suggestedLabel: 'Suggested Connection Note',
    icon: 'icon-[material-symbols--person-add-outline]',
    badge: 'bg-base-200 text-base-content/70',
    chip: 'bg-base-200 text-base-content/70',
  },
  linkedin_dm: {
    label: 'LinkedIn DM',
    previewLabel: 'LinkedIn Direct Message',
    suggestedLabel: 'Suggested LinkedIn Message',
    icon: 'icon-[material-symbols--chat-outline]',
    badge: 'bg-base-200 text-base-content/70',
    chip: 'bg-base-200 text-base-content/70',
  },
};

// Lanes (email on one side, LinkedIn on the other), the per-lane numbering
// and labels, the wait's unit word and the connect-note limit:
// logic/sequence.ts.

// ---------------------------------------------------------------------------
// Variable bar
// ---------------------------------------------------------------------------

/**
 * Renders a horizontal bar of draggable, clickable variable chips.
 * `onInsert` receives just the variable key (e.g. "first_name").
 */
function renderVariableChip(
  v: { key: string; label: string },
  onInsert: (key: string) => void,
  theme: { bg: string; color: string; border: string },
) {
  return html`
    <span
      class="cursor-pointer rounded-md px-2 py-0.5 text-xs font-medium transition-colors select-none"
      style="background:${theme.bg}; color:${theme.color}; border:${theme.border};"
      draggable="true"
      title="Click to insert or drag into prompt"
      @dragstart=${(e: DragEvent) => {
        e.dataTransfer?.setData('text/plain', `{{${v.key}}}`);
        e.dataTransfer!.effectAllowed = 'copy';
      }}
      @click=${() => onInsert(v.key)}
      >${v.label}</span
    >
  `;
}

function renderVariableBar(onInsert: (key: string) => void) {
  return html`
    <div class="flex flex-wrap items-center gap-1.5 py-1">
      <span class="text-base-content/40 text-xs select-none">Variables:</span>
      ${TEMPLATE_VARIABLES.map(v => renderVariableChip(v, onInsert, VARIABLE_CHIP_THEME))}
      <span class="text-base-content/40 ml-2 text-xs select-none">Sender:</span>
      ${SENDER_VARIABLES.map(v => renderVariableChip(v, onInsert, SENDER_CHIP_THEME))}
    </div>
  `;
}

const channelsForType = () => CHANNELS_ALL;

// Conditions are derived automatically (no per-step UI): logic/sequence.ts.

// ---------------------------------------------------------------------------
// Per-step example generation (always-on preview)
// ---------------------------------------------------------------------------

interface GeneratedExample {
  subject: string;
  body: string;
}

// The sample lead, the content key of a stored preview, and the preview's
// request are logic/sequence.ts's.

/** Render a realistic sample of this step's message via the backend LLM. */
async function generateExample(
  step: SequenceStep,
  offering: OfferingConfig,
  sampleLead?: SampleLead | null,
  language?: string,
  aiEmployeeId?: string,
): Promise<GeneratedExample> {
  // The step with its refine instruction, written for the sample lead (logic/sequence.ts).
  const request = previewRequest(step, offering, sampleLead, language, aiEmployeeId);
  if (!request) return { subject: '', body: '' };
  const res = await fetch('/api/prompt/test', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  const data = (await res.json()) as { ok: boolean; subject?: string; body?: string; error?: string };
  if (!data.ok) throw new Error(data.error ?? 'Could not generate a preview.');
  return { subject: (data.subject ?? '').trim(), body: (data.body ?? '').trim() };
}

// ---------------------------------------------------------------------------
// SequenceStepCard — one step (compact or expanded). Owns its own always-on
// preview generation via useQuery so every step shows a real sample message,
// and edits happen purely through AI actions (no raw prompt / variables shown).
// ---------------------------------------------------------------------------

interface StepCardProps {
  step: SequenceStep;
  descriptor: StepDescriptor;
  expanded: boolean;
  isFirst: boolean;
  isLast: boolean;
  offering: OfferingConfig;
  /** Real lead used to personalize the preview (null while none is available). */
  sampleLead: SampleLead | null;
  /** True while a sample lead is still being fetched from Apollo. */
  sampleLeadLoading: boolean;
  /** Outreach language (ISO code) the previews are generated in. */
  language?: string;
  /** The draft's LinkedIn accounts, so the note notice names only those. */
  linkedinAccountIds?: readonly string[];
  /** The campaign's selected rep, so the preview signs off as that rep. */
  previewAiEmployeeId?: string;
  onToggle: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onPatch: (patch: Partial<SequenceStep>) => void;
}

const SequenceStepCard = stateful((props: StepCardProps) => {
  const {
    step,
    descriptor,
    expanded,
    isFirst,
    isLast,
    offering,
    sampleLead,
    sampleLeadLoading,
    language,
    previewAiEmployeeId,
    onToggle,
    onRemove,
    onMoveUp,
    onMoveDown,
    onPatch,
  } = props;
  const meta = CHANNEL_META[step.channel];
  const isEmail = step.channel === 'email';
  const isConnect = step.channel === 'linkedin_connect';

  const [nonce, setNonce] = useState(0);
  const [improving, setImproving] = useState<'body' | 'subject' | null>(null);
  // The refine instruction lives on the step (not in component state) so it
  // stays visible across wizard navigation and re-opening the campaign.
  const instruction = step.refineInstruction ?? '';
  // Overlay showing the full refine instruction (long prompts don't fit the inline field).
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  // True once an AI refinement (a prompt, Shorten or Make-it-friendly) has been
  // applied to the current sample, so the preview can be visibly greyed as a
  // "this was refined" cue. A full Regenerate clears it — a fresh sample is not
  // a refinement. Local state on purpose: it is a just-refined hint, not a fact
  // that must survive navigation.
  const [refinementApplied, setRefinementApplied] = useState(false);
  // Manual edit mode — lets the user rewrite the ACTUAL message text (sent as
  // written, variables interpolated per lead) instead of only steering the AI.
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const isManual = !!step.manualBody?.trim();

  // Serve the sample persisted on the step. It only regenerates when the user
  // explicitly triggers it (Generate/Shorten/Friendly/Regenerate), not on
  // wizard navigation or by simply re-opening an existing campaign draft.
  // `sampleLeadKey` only matters for the FIRST generation of a brand-new step:
  // it defers that call until the sample lead finished loading so the very
  // first preview is already personalized with a real person.
  const sampleLeadKey = sampleLead
    ? `${sampleLead.firstName ?? ''}|${sampleLead.company ?? ''}`
    : sampleLeadLoading
      ? 'loading'
      : 'none';
  const contentKey = previewContentKey(step, offering);
  const preview = useQuery<GeneratedExample>(async () => {
    // Manual mode renders step.manualBody directly — never call the LLM.
    if (isManual) return { subject: step.previewSubject ?? '', body: step.previewBody ?? '' };
    if (nonce === 0 && step.previewBody) {
      return { subject: step.previewSubject ?? '', body: step.previewBody };
    }
    // No stored preview yet and the sample lead is still loading — hold off so
    // the first generated sample already uses the real lead.
    if (nonce === 0 && sampleLeadLoading) return { subject: '', body: '' };
    const fresh = await generateExample(step, offering, sampleLead, language, previewAiEmployeeId);
    if (fresh.subject || fresh.body) {
      onPatch({ previewSubject: fresh.subject, previewBody: fresh.body, previewKey: contentKey });
    }
    return fresh;
  }, [step.id, isManual, nonce, sampleLeadKey, language ?? 'en', previewAiEmployeeId ?? '']);
  const busy = improving !== null || preview.loading;

  const improve = async (mode: 'shorten' | 'friendly' | 'instruction' | 'subject', freeInstruction?: string) => {
    setImproving(mode === 'subject' ? 'subject' : 'body');
    try {
      const res = await fetch('/api/prompt/improve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(improveRequest(step, mode, freeInstruction)),
      });
      const data = (await res.json()) as { ok: boolean; text?: string; error?: string };
      if (data.ok && data.text) {
        onPatch(mode === 'subject' ? { subject: data.text } : { prompt: data.text });
        // A body refinement (prompt / Shorten / Friendly) greys the preview; a
        // subject-only tweak leaves the message body as-is, so it does not.
        if (mode !== 'subject') setRefinementApplied(true);
        // Regenerate preview only after an explicit user action.
        setNonce(nonce + 1);
      } else {
        toast.error(data.error ?? 'AI request failed. Please try again.');
      }
    } catch {
      toast.error('Network error — could not reach the AI service.');
    } finally {
      setImproving(null);
    }
  };

  const runInstruction = () => {
    const text = instruction.trim();
    if (!text) {
      toast.info('Describe the change you want, then press Generate.');
      return;
    }
    // Keep the instruction in the input so the user still sees what they asked
    // for after the message regenerates.
    void improve('instruction', text);
  };

  const skeletonLines = (n: number) => html`
    <div class="flex flex-col gap-2">
      ${Array.from({ length: n }).map(() => html`<div class="skeleton h-3.5 w-full"></div>`)}
      <div class="skeleton h-3.5 w-2/3"></div>
    </div>
  `;

  // ── Compact ────────────────────────────────────────────────
  if (!expanded) {
    // Manual messages are shown as written; AI steps show the stored sample.
    const body = step.manualBody?.trim() || (preview.value?.body?.trim() ?? '');
    return html`
      <div
        class="card bg-base-100 cursor-pointer"
        @click=${onToggle}
      >
        <div class="card-body gap-2 p-3">
          <div class="flex items-center gap-2">
            <span
              class="${meta.badge} flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-medium tabular-nums"
            >
              #${descriptor.number}
            </span>
            <span
              class="${meta.chip} inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium"
            >
              <span class="${meta.icon} text-sm"></span>
              ${descriptor.label}
            </span>
            <div class="flex-1"></div>
            <button
              class="btn btn-ghost btn-xs btn-square text-base-content/40 hover:text-error"
              title="Delete step"
              @click=${(e: Event) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <span class="icon-[material-symbols--delete-outline] text-base"></span>
            </button>
            <span class="icon-[material-symbols--expand-more] text-base-content/40 text-base"></span>
          </div>
          <div class="text-base-content/50 text-xs font-medium">${meta.previewLabel}</div>
          ${
            body
              ? html`<p class="text-base-content/80 line-clamp-3 text-sm whitespace-pre-wrap">${body}</p>`
              : preview.loading
                ? skeletonLines(2)
                : preview.error
                  ? html`<p class="text-error/80 text-sm">Could not generate preview — open to retry.</p>`
                  : html`<p class="text-base-content/40 text-sm italic">No preview yet.</p>`
          }
        </div>
      </div>
    `;
  }

  // ── Expanded ───────────────────────────────────────────────
  return html`
    <div class="card bg-base-100 ndi-card-active">
      <div class="card-body flex flex-col gap-4 p-4">
        <!-- header -->
        <div class="flex items-center gap-2">
          <span
            class="${meta.badge} flex h-7 min-w-7 items-center justify-center rounded-full px-1.5 text-sm font-medium tabular-nums"
          >
            #${descriptor.number}
          </span>
          <span
            class="${meta.chip} inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium"
          >
            <span class="${meta.icon} text-sm"></span>
            ${descriptor.label}
          </span>
          <div class="flex-1"></div>
          <button class="btn btn-ghost btn-xs btn-square" ?disabled=${isFirst} @click=${onMoveUp} title="Move up">
            <span class="icon-[material-symbols--arrow-upward] text-base"></span>
          </button>
          <button class="btn btn-ghost btn-xs btn-square" ?disabled=${isLast} @click=${onMoveDown} title="Move down">
            <span class="icon-[material-symbols--arrow-downward] text-base"></span>
          </button>
          <button
            class="btn btn-ghost btn-xs btn-square text-base-content/50 hover:text-error"
            @click=${onRemove}
            title="Delete step"
          >
            <span class="icon-[material-symbols--delete-outline] text-base"></span>
          </button>
          <button class="btn btn-ghost btn-xs btn-square" @click=${onToggle} title="Collapse">
            <span class="icon-[material-symbols--expand-less] text-base"></span>
          </button>
        </div>

        <!-- suggested header + regenerate + manual edit -->
        <div class="flex items-center gap-2">
          <span class="${meta.icon} text-base-content/60 text-base"></span>
          <span class="text-sm font-medium">${meta.suggestedLabel}</span>
          ${
            isManual
              ? html`<span class="text-base-content/60 inline-flex items-center gap-1 text-xs">
                  <span class="icon-[material-symbols--edit] text-xs"></span> Manual
                </span>`
              : nothing
          }
          <div class="flex-1"></div>
          ${
            editing
              ? nothing
              : html`
                <button
                  class="btn btn-ghost btn-xs gap-1"
                  ?disabled=${busy}
                  title="Rewrite the message text yourself — it will be sent exactly as you write it"
                  @click=${() => {
                    setEditSubject(isManual ? step.subject : preview.value?.subject || step.subject);
                    setEditBody(step.manualBody || preview.value?.body || '');
                    setEditing(true);
                  }}
                >
                  <span class="icon-[material-symbols--edit] text-sm"></span>
                  Edit message
                </button>
                ${
                  isManual
                    ? html`
                      <button
                        class="btn btn-ghost btn-xs gap-1"
                        title="Discard the manual text and let the AI personalize each lead's message again"
                        @click=${() => onPatch({ manualBody: undefined })}
                      >
                        <span class="icon-[material-symbols--auto-awesome] text-sm"></span>
                        Back to AI
                      </button>
                    `
                    : html`
                      <button
                        class="btn btn-ghost btn-xs gap-1"
                        ?disabled=${busy}
                        title="Generate a fresh sample"
                        @click=${() => {
                          // Full regenerate resets the ad-hoc refine input and the
                          // "refined" grey — this produces a fresh, un-refined sample.
                          onPatch({ refineInstruction: '' });
                          setRefinementApplied(false);
                          setNonce(nonce + 1);
                        }}
                      >
                        ${
                          preview.loading && improving === null
                            ? html`<span class="loading loading-spinner loading-xs"></span>`
                            : html`<span class="icon-[material-symbols--autorenew] text-sm"></span>`
                        }
                        Regenerate
                      </button>
                    `
                }
              `
          }
        </div>

        <!--
          Where this text actually lands, on every connect step.

          This was a one-line grey hint and has been raised to a full notice, at
          the customer's request: it was too quiet for the only screen where the
          person can still act on it. Still an INFO alert, never a warning or an
          error — nothing here is a mistake the user made, and an error state
          would be a lie about whose problem this is.

          Shown only when all three hold: a connect step, a note actually
          written in it, and a sending account that is not a paid seat. The
          third is the component's to judge; the first two are ours, because
          only this card knows the step. See connection-note-notice.ts for the
          three reasons it distinguishes, why it carries no number, and why it
          does not claim the note is re-sent after acceptance.
        -->
        ${
          isConnect
            ? ConnectionNoteNotice({
              hasNoteOnConnectStep: connectStepCarriesANote(step),
              // Scoped to this draft's accounts. Absent while the Sales Rep
              // step is unanswered, and then the notice renders nothing rather
              // than naming every colleague in the workspace.
              sendingAccountIds: props.linkedinAccountIds ?? [],
            })
            : nothing
        }

        ${
          editing
            ? html`
              <!-- manual edit mode -->
              <div class="border-base-300 flex flex-col gap-3 rounded-xl border p-3">
                ${
                  isEmail
                    ? html`
                      <div class="flex flex-col gap-1">
                        <span class="text-xs font-medium">Subject</span>
                        <input
                          type="text"
                          class="input input-bordered input-sm w-full"
                          .value=${editSubject}
                          @input=${(e: Event) => setEditSubject((e.target as HTMLInputElement).value)}
                        />
                      </div>
                    `
                    : nothing
                }
                <div class="flex flex-col gap-1">
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-medium">Message</span>
                    ${
                      isConnect
                        ? html`<span
                            class="text-xs ${editBody.length > LINKEDIN_CONNECT_NOTE_MAX_CHARS ? 'text-error font-medium' : 'text-base-content/50'}"
                          >
                            ${editBody.length} / ${LINKEDIN_CONNECT_NOTE_MAX_CHARS}
                          </span>`
                        : nothing
                    }
                  </div>
                  <p class="text-base-content/50 text-xs">
                    This exact text is sent to every lead — no AI rewriting. Variables like
                    <code class="bg-base-200 rounded px-1">{{first_name}}</code> are replaced with each lead's real
                    data.
                  </p>
                  ${
                    isConnect && editBody.length > LINKEDIN_CONNECT_NOTE_MAX_CHARS
                      ? html`
                        <div class="alert alert-error py-2 text-sm">
                          <span class="icon-[material-symbols--warning-outline] text-lg"></span>
                          <span>
                            Too many characters — LinkedIn connection requests are limited to
                            ${LINKEDIN_CONNECT_NOTE_MAX_CHARS} characters. Shorten the message to continue.
                          </span>
                        </div>
                      `
                      : nothing
                  }
                  <prompt-editor
                    data-editor-id="step-body-${step.id}"
                    .value=${editBody}
                    .rows=${8}
                    @value-changed=${(e: CustomEvent<string>) => setEditBody(e.detail ?? '')}
                  ></prompt-editor>
                  ${renderVariableBar(key => {
                    const el = document.querySelector(
                      `[data-editor-id="step-body-${step.id}"]`,
                    ) as PromptEditorElement | null;
                    el?.insertVariable(key);
                  })}
                </div>
                <div class="flex justify-end gap-2">
                  <button class="btn btn-ghost btn-sm" @click=${() => setEditing(false)}>Cancel</button>
                  <button
                    class="btn btn-primary btn-sm gap-1"
                    ?disabled=${!editBody.trim() || (isEmail && !editSubject.trim()) || (isConnect && editBody.length > LINKEDIN_CONNECT_NOTE_MAX_CHARS)}
                    @click=${() => {
                      onPatch({ manualBody: editBody, subject: editSubject });
                      setEditing(false);
                    }}
                  >
                    <span class="icon-[material-symbols--check] text-sm"></span>
                    Save message
                  </button>
                </div>
              </div>
            `
            : html`
        <!-- subject (email only) -->
        ${
          isEmail
            ? html`
              <div class="bg-base-200 flex items-center gap-2 rounded-lg px-3 py-2">
                <span class="text-base-content/50 text-xs font-medium">Subject</span>
                ${
                  isManual
                    ? html`<span class="flex-1 text-sm font-medium">${step.subject || '—'}</span>`
                    : preview.loading
                      ? html`<div class="skeleton h-4 flex-1"></div>`
                      : html`<span class="flex-1 text-sm font-medium">${preview.value?.subject || '—'}</span>`
                }
              </div>
            `
            : nothing
        }

        <!-- generated message -->
        <div class="border-base-300 relative rounded-lg border p-4">
          <div class="text-base-content/60 mb-2 flex items-center gap-1.5 text-xs font-medium">
            <span class="${isManual ? 'icon-[material-symbols--edit]' : 'icon-[material-symbols--auto-awesome]'} text-sm"></span>
            ${isManual ? 'Manual message — sent as written' : 'AI-suggested message'}
            ${
              // A quiet marker that the shown message is the result of a refine,
              // paired with the greyed body below so the grey is not read as
              // "stale" or "disabled". Cleared by a full Regenerate.
              !isManual && refinementApplied
                ? html`<span class="text-base-content/50 inline-flex items-center gap-0.5 font-normal">
                    <span class="icon-[material-symbols--check-small] text-sm"></span>Refined
                  </span>`
                : nothing
            }
          </div>
          ${
            isManual
              ? html`<div class="text-base-content/90 text-sm leading-relaxed whitespace-pre-wrap">${step.manualBody}</div>`
              : preview.loading
                ? skeletonLines(5)
                : preview.error
                  ? html`<p class="text-error text-sm">Could not generate this message. Try “Regenerate”.</p>`
                  : html`<div
                      class="${refinementApplied ? 'text-base-content/60' : 'text-base-content/90'} text-sm leading-relaxed whitespace-pre-wrap transition-colors"
                    >${preview.value?.body || 'No message generated yet.'}</div>`
          }
          <p class="text-base-content/50 border-base-300 mt-3 border-t pt-2 text-xs">
            <span class="icon-[material-symbols--info-outline] align-text-bottom text-sm"></span>
            ${
              isManual
                ? html`This text is sent exactly as written — only variables like {{first_name}} are replaced with each
                  lead's real data.`
                : sampleLead?.firstName
                  ? html`Preview personalized with a real lead from your list: <strong>${sampleLead.firstName}${sampleLead.company ? ` (${sampleLead.company})` : ''}</strong>. Every lead gets their own personalized version.`
                  : html`Every lead gets their own personalized version with their real name, company, and job title.`
            }
          </p>
          ${
            improving === 'body'
              ? html`<div
                  class="bg-base-100/70 absolute inset-0 flex items-center justify-center gap-2 rounded-lg text-sm backdrop-blur-[1px]"
                >
                  <span class="loading loading-spinner loading-sm"></span> Updating…
                </div>`
              : nothing
          }
        </div>

        <!-- improve with AI (not applicable while a manual message is set) -->
        ${
          isManual
            ? nothing
            : html`
        <div class="bg-base-200 flex flex-col gap-2 rounded-xl p-3">
          <div class="flex items-start gap-2">
            <div class="relative flex-1">
              <textarea
                class="textarea textarea-bordered textarea-sm max-h-40 min-h-9 w-full resize-none overflow-y-auto py-1.5 leading-snug"
                rows="1"
                placeholder="Make changes to the content through prompts!"
                .value=${instruction}
                ?disabled=${busy}
                @input=${(e: Event) => {
                  const el = e.target as HTMLTextAreaElement;
                  // Auto-grow with the content (capped by max-h, then scrolls).
                  el.style.height = 'auto';
                  el.style.height = `${el.scrollHeight}px`;
                  onPatch({ refineInstruction: el.value });
                }}
                @keydown=${(e: KeyboardEvent) => {
                  // Enter runs the prompt; Shift+Enter inserts a newline.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    runInstruction();
                  }
                }}
              ></textarea>
            </div>
            <button class="btn btn-primary btn-sm gap-1 shrink-0" ?disabled=${busy} @click=${runInstruction}>
              <span class="icon-[material-symbols--auto-awesome] text-sm"></span>
              Generate
            </button>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <button class="btn btn-xs gap-1" ?disabled=${busy} @click=${() => improve('shorten')}>
              <span class="icon-[material-symbols--compress] text-sm"></span>
              Shorten
            </button>
            <button class="btn btn-xs gap-1" ?disabled=${busy} @click=${() => improve('friendly')}>
              <span class="icon-[material-symbols--sentiment-satisfied-outline] text-sm"></span>
              Make it friendly
            </button>
            ${
              instruction.length > 150
                ? html`
                  <button
                    class="btn btn-ghost btn-xs ml-auto gap-1"
                    title="Show the full prompt in an overlay"
                    @click=${() => setShowFullPrompt(true)}
                  >
                    <span class="icon-[material-symbols--open-in-full] text-sm"></span>
                    Expand prompt
                  </button>
                `
                : nothing
            }
          </div>
        </div>
        ${
          showFullPrompt
            ? html`
              <div
                class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                @click=${() => setShowFullPrompt(false)}
              >
                <div
                  class="ndi-dialog flex max-h-[80vh] w-full max-w-2xl flex-col gap-3 p-6"
                  @click=${(e: Event) => e.stopPropagation()}
                >
                  <div class="flex items-center gap-2">
                    <span class="icon-[material-symbols--auto-awesome] text-base-content/50 text-lg"></span>
                    <h3 class="text-[0.9375rem] font-medium">Refine prompt</h3>
                    <div class="flex-1"></div>
                    <button class="btn btn-ghost btn-xs btn-square" @click=${() => setShowFullPrompt(false)}>
                      <span class="icon-[material-symbols--close] text-base"></span>
                    </button>
                  </div>
                  <textarea
                    class="textarea textarea-bordered min-h-48 w-full flex-1 text-sm leading-relaxed"
                    .value=${instruction}
                    ?disabled=${busy}
                    @input=${(e: Event) => onPatch({ refineInstruction: (e.target as HTMLTextAreaElement).value })}
                  ></textarea>
                  <div class="flex justify-end gap-2">
                    <button class="btn btn-ghost btn-sm" @click=${() => setShowFullPrompt(false)}>Close</button>
                    <button
                      class="btn btn-primary btn-sm gap-1"
                      ?disabled=${busy}
                      @click=${() => {
                        setShowFullPrompt(false);
                        runInstruction();
                      }}
                    >
                      <span class="icon-[material-symbols--auto-awesome] text-sm"></span>
                      Generate
                    </button>
                  </div>
                </div>
              </div>
            `
            : nothing
        }
        `
        }
        `
        }
      </div>
    </div>
  `;
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const WizSequence = stateful((props: WizSequenceProps) => {
  const {
    sequence,
    leads,
    offering,
    onSequenceChange,
    autoReply,
    onAutoReplyChange,
    replyPrompt,
    onReplyPromptChange,
  } = props;
  const hasEmailAccount = props.hasEmailAccount ?? true;
  const hasLinkedinAccount = props.hasLinkedinAccount ?? true;

  // Local UI state
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [editingDelayIdx, setEditingDelayIdx] = useState<number | null>(null);
  const [insertMenuAt, setInsertMenuAt] = useState<number | null>(null);

  // ── Sample lead for realistic previews ─────────────────────────────
  // Message previews should read like the real thing — “Hi Ariaan” instead of
  // “Hi there”. Prefer the first uploaded/imported lead; when the user sourced
  // via Apollo instead, fetch one matching lead from the saved criteria.
  const uploadedSample: SampleLead | null = uploadedSampleLead(leads);
  const apolloCriteria = !uploadedSample && props.apolloSearchInput ? props.apolloSearchInput : null;
  const apolloSampleQuery = useQuery<SampleLead | null>(async () => {
    if (!apolloCriteria) return null;
    try {
      // The search the lead step previews (sourcing-search.ts): the company
      // list, the revenue, the removed people left out, an interpreted value
      // left to the model. It was a copy of its own without the first three,
      // so the sample could be someone the campaign will never source (R17a).
      const entity = sourcingSearch(apolloCriteria, { page: 1, perPage: 1 });
      const result = await searchApolloLeads(entity);
      return sourcedSampleLead(result?.leads?.[0]);
    } catch {
      return null; // preview falls back to generic copy — never block the step
    }
  }, [JSON.stringify(apolloCriteria ?? null)]);
  const sampleLead = uploadedSample ?? apolloSampleQuery.value ?? null;
  const sampleLeadLoading = !uploadedSample && !!apolloCriteria && apolloSampleQuery.loading;

  const descriptors = describeSteps(sequence);

  /** Single mutation path — conditions are always re-derived automatically. */
  const commit = (steps: SequenceStep[]) => onSequenceChange(normalizeConditions(steps));

  // Step cards patch asynchronously (a generated sample is written back onto
  // the step once the LLM responds). Those writebacks must never run through
  // a render-time closure — they would resurrect an old sequence and wipe
  // whatever happened while the request was in flight (other cards generating
  // in parallel, steps added or moved, instructions typed). `live` is a
  // stable ref that always points at the current render's sequence + commit.
  const [live] = useState({ sequence, commit });
  live.sequence = sequence;
  live.commit = commit;

  // ── Helpers ─────────────────────────────────────────────────────────────

  /** Patch one step by id against the LATEST sequence — safe for async callers. */
  const patchStep = (stepId: string, patch: Partial<SequenceStep>) => {
    live.commit(live.sequence.map(s => (s.id === stepId ? { ...s, ...patch } : s)));
  };

  const updateStep = (idx: number, patch: Partial<SequenceStep>) => {
    commit(sequence.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  // A new step, inserted, removed or moved: logic/sequence.ts.
  const insertStepAt = (pos: number, channel: SequenceChannel) => {
    commit(withStepInserted(sequence, pos, newStep(channel, pos, sequence, crypto.randomUUID())));
    setInsertMenuAt(null);
    setExpandedIdx(pos); // auto-open the freshly added step
  };

  const addStep = (channel: SequenceChannel) => insertStepAt(sequence.length, channel);

  const removeStep = (idx: number) => {
    const next = withoutStep(sequence, idx, expandedIdx);
    commit(next.sequence);
    if (next.expanded !== expandedIdx) setExpandedIdx(next.expanded);
  };

  const moveStep = (fromIdx: number, toIdx: number) => {
    const next = withStepMoved(sequence, fromIdx, toIdx);
    if (!next) return;
    commit(next);
    setExpandedIdx(toIdx);
  };

  const toggleExpand = (idx: number) => {
    setExpandedIdx(expandedIdx === idx ? null : idx);
    setEditingDelayIdx(null);
    setInsertMenuAt(null);
  };

  // Validation warnings, and the hard blockers (a channel the sequence uses
  // with no account behind it for the assigned Sales Representative(s)):
  // logic/sequence.ts.
  const warnings = (): string[] => sequenceWarnings(sequence);
  const accountErrors = (): string[] => sequenceAccountErrors(sequence, hasEmailAccount, hasLinkedinAccount);

  // Render -----------------------------------------------------------------

  // ── Lane render helpers ──────────────────────────────────────────────────

  /** “+” affordance between/around steps; expands to a small channel menu. */
  const renderInsertZone = (pos: number) => {
    const open = insertMenuAt === pos;
    return html`
      <div class="relative z-10 flex flex-col items-center py-1.5">
        ${
          open
            ? html`
              <div class="bg-base-100 border-(--sdr-line) z-10 flex flex-col gap-1 rounded-box border p-2 shadow-lg">
                <span class="text-base-content/50 px-2 pb-1 text-xs font-medium">Add a step here</span>
                ${CHANNELS_ALL.map(ch => {
                  const meta = CHANNEL_META[ch.value];
                  return html`
                    <button
                      class="hover:bg-(--sdr-hover) flex items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm transition-colors"
                      @click=${() => insertStepAt(pos, ch.value)}
                    >
                      <span class="${meta.chip} flex h-6 w-6 items-center justify-center rounded-md">
                        <span class="${meta.icon} text-sm"></span>
                      </span>
                      ${meta.label}
                    </button>
                  `;
                })}
                <button
                  class="text-base-content/40 hover:text-base-content mt-0.5 px-3 py-1 text-left text-xs"
                  @click=${() => setInsertMenuAt(null)}
                >
                  Cancel
                </button>
              </div>
            `
            : html`
              <button
                class="btn btn-circle btn-xs"
                title="Add step here"
                @click=${() => {
                  setInsertMenuAt(pos);
                  setEditingDelayIdx(null);
                }}
              >
                <span class="icon-[material-symbols--add] text-base"></span>
              </button>
            `
        }
      </div>
    `;
  };

  /** “Wait N days” connector chip (delay BEFORE step i); click to edit inline. */
  const renderWaitChip = (i: number) => {
    const step = sequence[i];
    if (!step) return nothing;
    if (editingDelayIdx === i) {
      return html`
        <div class="relative z-10 flex items-center justify-center py-1.5">
          <div class="bg-base-100 border-base-300 flex items-center gap-2 rounded-full border px-3 py-1">
            <span class="icon-[material-symbols--schedule] text-base-content/50 text-sm"></span>
            <span class="text-base-content/70 text-xs font-medium">Wait</span>
            <input
              type="number"
              min="0"
              max="9999"
              .value=${String(step.delayValue)}
              class="input input-xs w-14 text-center"
              @change=${(e: Event) =>
                updateStep(i, { delayValue: Math.max(0, Number((e.target as HTMLInputElement).value) || 0) })}
            />
            <select
              class="select select-xs"
              .value=${step.delayUnit}
              @change=${(e: Event) => updateStep(i, { delayUnit: (e.target as HTMLSelectElement).value as DelayUnit })}
            >
              <!--
                The selectedness is set on the OPTION, not left to the
                \`.value\` binding above. lit applies element properties before
                it commits child templates, so on the render that opens this
                editor \`.value\` is assigned while the option list is still
                empty — the browser drops it and falls back to the first option.
                The dropdown then reads "Minutes" for a step whose unit is
                "days", which is a control that lies about its own state.

                Reported 17 August: "I enter Wait 10 minutes, click OK, and it
                says 10 days". Nothing had gone wrong on save — the field had
                always said days, and the box showing Minutes never fired a
                change event because it was never actually changed.
              -->
              ${DELAY_UNITS.map(
                u => html`<option value=${u.value} .selected=${u.value === step.delayUnit}>${u.label}</option>`,
              )}
            </select>
            <button
              class="btn btn-xs btn-circle btn-ghost"
              title="Done"
              @click=${() => setEditingDelayIdx(null)}
            >
              <span class="icon-[material-symbols--check] text-sm"></span>
            </button>
          </div>
        </div>
      `;
    }
    return html`
      <div class="relative z-10 flex items-center justify-center py-1.5">
        <button
          class="border-base-300 bg-base-100 text-base-content/70 hover:border-(--sdr-line-strong) hover:text-base-content inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors"
          title="Edit the delay before this step"
          @click=${() => {
            setEditingDelayIdx(i);
            setInsertMenuAt(null);
          }}
        >
          <span class="icon-[material-symbols--schedule] text-sm"></span>
          Wait ${step.delayValue} ${unitWord(step.delayValue, step.delayUnit)}
        </button>
      </div>
    `;
  };

  const renderImmediatePill = () => html`
    <div class="relative z-10 flex items-center justify-center py-1.5">
      <span
        class="border-base-300 bg-base-100 text-base-content/50 inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium"
      >
        <span class="icon-[material-symbols--bolt] text-base-content/50 text-sm"></span>
        Starts immediately
      </span>
    </div>
  `;

  // Empty state
  if (sequence.length === 0) {
    return html`
      <div class="flex flex-col gap-6">
        <div class="card bg-base-100">
          <div class="card-body flex flex-col items-center gap-4 py-10 text-center">
            <span class="icon-[material-symbols--account-tree] text-base-content/20 text-5xl"></span>
            <div>
              <h2 class="text-[0.9375rem] font-medium">Build your outreach sequence</h2>
              <p class="text-base-content/70 mt-1 text-sm">
                Define the steps, timing, and AI prompts for your multi-channel outreach.
              </p>
            </div>

            <div class="mt-2 flex flex-wrap justify-center gap-3">
              <button
                class="btn gap-2"
                @click=${() => onSequenceChange(defaultEmailSequence())}
              >
                <span class="icon-[material-symbols--bolt] text-lg"></span>
                Quick Start: Email (3 Steps)
              </button>
              <button
                class="btn gap-2"
                @click=${() => onSequenceChange(defaultLinkedinSequence())}
              >
                <span class="icon-[material-symbols--bolt] text-lg"></span>
                Quick Start: LinkedIn (2 Steps)
              </button>
              <button class="btn gap-2" @click=${() => onSequenceChange(defaultMultiSequence())}>
                <span class="icon-[material-symbols--bolt] text-lg"></span>
                Quick Start: Cross-Channel (4 Steps)
              </button>
            </div>

            <div class="divider text-base-content/40 text-xs">or add steps manually</div>

            <div class="flex flex-wrap gap-2">
              ${channelsForType().map(
                ch => html`
                  <button class="btn btn-sm btn-ghost gap-1" @click=${() => addStep(ch.value)}>
                    <span class="icon-[material-symbols--add] text-base"></span>
                    ${ch.label}
                  </button>
                `,
              )}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // Main sequence view
  const warn = warnings();
  const acctErrors = accountErrors();

  return html`
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-3">
        <span class="bg-base-200 text-base-content/60 flex h-10 w-10 items-center justify-center rounded-lg">
          <span class="icon-[material-symbols--account-tree] text-xl"></span>
        </span>
        <div>
          <h2 class="text-[0.9375rem] font-medium">Outreach sequence</h2>
          <p class="text-base-content/70 text-sm">Every step auto-generates a sample message you can refine with AI.</p>
        </div>
        <div class="flex-1"></div>
        <label class="flex items-center gap-1.5" title="All messages, previews and auto-replies are written in this language">
          <span class="icon-[material-symbols--language] text-base-content/60 text-lg"></span>
          <select
            class="select select-bordered select-sm"
            .value=${props.language || 'en'}
            @change=${(e: Event) => {
              const next = (e.target as HTMLSelectElement).value;
              if (next === (props.language || 'en')) return;
              // The parent applies the language AND clears the stored previews
              // in ONE state update — two separate updates raced against each
              // other and occasionally lost one of the changes.
              props.onLanguageChange?.(next);
            }}
          >
            <!-- Same reason as the delay unit above: selectedness belongs on the option. -->
            ${OUTREACH_LANGUAGES.map(
              l => html`<option value=${l.code} .selected=${l.code === (props.language || 'en')}>${l.label}</option>`,
            )}
          </select>
        </label>
        <button
          class="btn btn-sm btn-ghost text-error gap-1"
          title="Remove all steps"
          @click=${() => {
            onSequenceChange([]);
            setExpandedIdx(null);
          }}
        >
          <span class="icon-[material-symbols--delete-outline] text-base"></span>
          Clear all
        </button>
      </div>

      <!-- Account blockers (must be fixed before continuing) -->
      ${
        acctErrors.length > 0
          ? html`
            <div class="alert alert-error text-sm">
              <span class="icon-[material-symbols--block] text-lg"></span>
              <div class="flex flex-col gap-0.5">
                ${acctErrors.map(e => html`<span class="font-medium">${e}</span>`)}
                <span class="text-xs opacity-80">
                  Go back to “Sales Representatives” and connect/select an account, or remove the affected step.
                </span>
              </div>
            </div>
          `
          : nothing
      }

      <!-- Warnings -->
      ${
        warn.length > 0
          ? html`
            <div class="alert alert-warning text-sm">
              <span class="icon-[material-symbols--warning] text-lg"></span>
              <div class="flex flex-col gap-0.5">${warn.map(w => html`<span>⚠ ${w}</span>`)}</div>
            </div>
          `
          : nothing
      }

      <!-- Sequence canvas — a single connected flow; a dashed spine links every step -->
      <div class="border-(--sdr-line) overflow-x-auto rounded-box border p-4 sm:p-6">
        <div class="relative mx-auto flex w-full max-w-2xl flex-col">
          <!-- continuous dashed flow line behind the steps -->
          <div
            class="border-base-content/20 pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 border-l border-dashed"
          ></div>
          ${renderInsertZone(0)}
          ${sequence.map((step, i) => {
            const descriptor = descriptors[i];
            if (!descriptor) return nothing;
            const expanded = expandedIdx === i;
            const isLinkedIn = laneOf(step.channel) === 'linkedin';
            const card = SequenceStepCard({
              step,
              descriptor,
              expanded,
              isFirst: i === 0,
              isLast: i === sequence.length - 1,
              offering,
              sampleLead,
              sampleLeadLoading,
              language: props.language,
              linkedinAccountIds: props.linkedinAccountIds,
              previewAiEmployeeId: props.previewAiEmployeeId,
              onToggle: () => toggleExpand(i),
              onRemove: () => removeStep(i),
              onMoveUp: () => moveStep(i, i - 1),
              onMoveDown: () => moveStep(i, i + 1),
              onPatch: patch => patchStep(step.id, patch),
            });
            // One connected flow with the spine down the middle: email steps are left-aligned,
            // LinkedIn steps right-aligned (both SAME width, so the left/right inset is identical).
            // Each card still overlaps the centre, so the dashed line runs into every card top/
            // bottom → connected, not detached. Expanded uses the full width for the editor.
            // Same wrapper template either way so expand/collapse never tears down the step's
            // preview state.
            const wrapperClass = expanded
              ? 'relative z-10 mx-auto w-full max-w-2xl'
              : isLinkedIn
                ? 'relative z-10 ml-auto mr-0 w-full max-w-md'
                : 'relative z-10 mr-auto ml-0 w-full max-w-md';
            return html`
              ${i > 0 ? renderWaitChip(i) : renderImmediatePill()}
              <div class="${wrapperClass}">${card}</div>
              ${renderInsertZone(i + 1)}
            `;
          })}
        </div>
      </div>

      <!-- Reply Settings -->
      <div class="card bg-base-100 mt-6">
        <div class="card-body p-6">
          <div class="flex items-center justify-between">
            <h3 class="flex items-center gap-2 text-[0.9375rem] font-medium">
              <span class="icon-[material-symbols--reply] text-base-content/50 text-lg"></span>
              Auto-generate reply drafts
            </h3>
            <input
              type="checkbox"
              class="toggle toggle-primary"
              ?checked=${!!autoReply}
              @change=${(e: Event) => onAutoReplyChange?.((e.target as HTMLInputElement).checked)}
            />
          </div>
          <p class="text-base-content/60 text-sm">
            When enabled, the AI agent automatically generates draft replies when leads respond to your campaign.
          </p>
          ${
            autoReply
              ? html`
                <div class="mt-3 flex flex-col gap-3">
                  <div>
                    <label class="label"><span class="label-text text-sm font-medium">Reply prompt</span></label>
                    <prompt-editor
                      data-editor-id="reply-prompt"
                      .value=${replyPrompt ?? ''}
                      .placeholder=${'Describe how the AI should respond when a lead replies...\n\nExample: "Respond professionally. If they are interested, suggest a demo call. If they have questions, answer them helpfully. Keep it under 120 words."'}
                      .rows=${4}
                      @value-changed=${(e: CustomEvent<string>) => onReplyPromptChange?.(e.detail ?? '')}
                    ></prompt-editor>
                    <!--
                      No variable chips here, unlike the outreach steps. A reply is
                      written against what THIS lead actually said, so a fixed set of
                      {{first_name}}-style placeholders doesn't fit the reply prompt —
                      it described a template the reply agent never fills that way.
                    -->
                  </div>
                </div>
              `
              : nothing
          }
        </div>
      </div>
    </div>
  `;
});
